import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {Set<string>} */
  static #preloadedImages = new Set();

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.productVariantMedia) {
      this.addEventListener('pointerenter', this.#preloadImage, { passive: true });
      this.addEventListener('focusin', this.#preloadImage);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) window.clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) window.clearTimeout(this.#cleanupTimeout);

    this.removeEventListener('pointerenter', this.#preloadImage);
    this.removeEventListener('focusin', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    const button = this.refs.addToCartButton;

    if (!button || button.disabled) return;

    button.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    const button = this.refs.addToCartButton;

    if (!button || !button.disabled) return;

    button.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (!this.#checkFormValidity()) return;

    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image || AddToCartComponent.#preloadedImages.has(image)) return;

    AddToCartComponent.#preloadedImages.add(image);
    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const image = this.dataset.productVariantMedia;

    if (!addToCartButton || !image) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!cartIcon) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (!addToCartButton) return;

    if (this.#animationTimeout) window.clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) window.clearTimeout(this.#cleanupTimeout);

    addToCartButton.classList.add('atc-added');

    this.#animationTimeout = window.setTimeout(() => {
      this.#cleanupTimeout = window.setTimeout(() => {
        this.refs.addToCartButton?.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Checks if the form is valid when the user adds an item to cart.
   * Currently only checks the gift card recipient form.
   * @returns {boolean} - True if the form is valid, false otherwise.
   */
  #checkFormValidity() {
    const form = this.closest('form');

    if (!form) return true;

    const recipientInputs = form.querySelectorAll(
      'input[id*="Recipient"], select[id*="Recipient"], textarea[id*="Recipient"]'
    );

    if (!recipientInputs.length) return true;

    for (const input of recipientInputs) {
      if (
        !(
          input instanceof HTMLInputElement ||
          input instanceof HTMLSelectElement ||
          input instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      if (input.disabled) continue;

      if (!input.checkValidity()) {
        return false;
      }
    }

    return true;
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #timeout;

  /** @type {number | undefined} */
  #liveRegionTimeout;

  /** @type {number | undefined} */
  #variantUpdateFrame;

  /** @type {VariantUpdateEvent | undefined} */
  #pendingVariantUpdateEvent;

  /** @type {boolean} */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#scheduleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;

    if (this.#timeout) window.clearTimeout(this.#timeout);
    if (this.#liveRegionTimeout) window.clearTimeout(this.#liveRegionTimeout);
    if (this.#variantUpdateFrame) window.cancelAnimationFrame(this.#variantUpdateFrame);

    this.#isSubmitting = false;
    this.#pendingVariantUpdateEvent = undefined;
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.#isSubmitting) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) {
      window.clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }

    const addToCartButtonContainer = this.refs.addToCartButtonContainer;
    const addToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (addToCartButton?.disabled) return;

    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    this.#isSubmitting = true;

    if (addToCartButtonContainer) {
      addToCartButtonContainer.disable();
    }

    const formData = new FormData(form);
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const id = formData.get('id');

    if (!id) throw new Error('Form ID is required');

    const productDetails = this.closest('.product-details');
    const selectedUpsell = productDetails?.querySelector('[data-chain-radio]:checked');
    const upsellVariantId = Number(selectedUpsell?.dataset.chainVariantId || selectedUpsell?.value);
    const hasUpsell = Number.isInteger(upsellVariantId) && upsellVariantId > 0;
    const addedItemCount = quantity + (hasUpsell ? 1 : 0);

    const cartItemComponentsSectionIds = this.#getCartItemSectionIds();
    let fetchCfg;

    if (hasUpsell) {
      const mainItem = {
        id: Number(id),
        quantity,
      };
      const properties = {};

      for (const [key, value] of formData.entries()) {
        const propertyMatch = key.match(/^properties\[(.+)\]$/);

        if (propertyMatch && value !== '') {
          properties[propertyMatch[1]] = value;
        }
      }

      if (Object.keys(properties).length) {
        mainItem.properties = properties;
      }

      const sellingPlan = formData.get('selling_plan');

      if (sellingPlan) {
        mainItem.selling_plan = Number(sellingPlan) || sellingPlan;
      }

      const requestBody = {
        items: [mainItem, { id: upsellVariantId, quantity: 1 }],
        sections_url: window.location.pathname,
      };

      if (cartItemComponentsSectionIds.length) {
        requestBody.sections = cartItemComponentsSectionIds.join(',');
      }

      fetchCfg = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(requestBody),
      };
    } else {
      if (cartItemComponentsSectionIds.length) {
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      }

      fetchCfg = fetchConfig('javascript', { body: formData });
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const responseJson = await response.json();

      if (responseJson.status) {
        const message = responseJson.message || '';

        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', message, responseJson.description, responseJson.errors)
        );

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent = message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(message));
          }
        }

        this.#setLiveRegionText(message);

        this.#timeout = window.setTimeout(() => {
          addToCartTextError?.classList.add('hidden');
          this.#clearLiveRegionText();
        }, 10000);

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: addedItemCount,
            productId: this.dataset.productId,
          })
        );

        return;
      }

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      if (addToCartButton) {
        const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        this.#liveRegionTimeout = window.setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);
      }

      this.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          source: 'product-form-component',
          itemCount: addedItemCount,
          productId: this.dataset.productId,
          sections: responseJson.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#isSubmitting = false;

      if (addToCartButtonContainer) {
        addToCartButtonContainer.enable();
      }

      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Gets the cart section IDs only once per submit.
   * @returns {string[]}
   */
  #getCartItemSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    return sectionIds;
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    if (this.#liveRegionTimeout) {
      window.clearTimeout(this.#liveRegionTimeout);
      this.#liveRegionTimeout = undefined;
    }

    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  /**
   * Schedules variant update DOM work in the next animation frame.
   * Keeps the interaction thread lighter when variant logic exists.
   *
   * @param {VariantUpdateEvent} event
   */
  #scheduleVariantUpdate = (event) => {
    this.#pendingVariantUpdateEvent = event;

    if (this.#variantUpdateFrame) return;

    this.#variantUpdateFrame = window.requestAnimationFrame(() => {
      this.#variantUpdateFrame = undefined;

      if (!this.#pendingVariantUpdateEvent) return;

      this.#onVariantUpdate(this.#pendingVariantUpdateEvent);
      this.#pendingVariantUpdateEvent = undefined;
    });
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate(event) {
    const detail = event.detail;
    const data = detail.data;
    const resource = detail.resource;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (!currentAddToCartButton) return;

    if (resource == null || resource.available === false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    const newAddToCartButton = data.html.querySelector('[ref="addToCartButton"]');

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    variantId.value = resource?.id ?? '';

    const productVariantMedia = resource?.featured_media?.preview_image?.src;

    if (productVariantMedia) {
      addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = {
      x: startPoint.x,
      y: startPoint.y - 200,
    };

    const controlPoint2 = {
      x: endPoint.x - 300,
      y: endPoint.y - 100,
    };

    /** @type {number | null} */
    let startTime = null;

    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);
      const scale = 1 - progress * 0.5;

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        window.requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    window.requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point.
 * @param {{x: number, y: number}} p2 - The second control point.
 * @param {{x: number, y: number}} p3 - The ending point.
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const t2 = t * t;
  const t3 = t2 * t;

  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * t3 + bX * t2 + cX * t + p0.x;
  const y = aY * t3 + bY * t2 + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
